"use client";

import { ShieldCheck, Lock, FileCheck2, Eye, AlertTriangle, KeyRound, ScrollText, Server } from "lucide-react";
import { SEED_AUDIT_LOG, CRM_ROLES, type AuditLogEntry } from "@/lib/demos/stockBrokingData";

const SEVERITY_STYLE: Record<AuditLogEntry["severity"], string> = {
  info: "bg-slate-500/15 text-slate-600 border-slate-500/25",
  warning: "bg-amber-500/15 text-amber-700 border-amber-500/25",
  critical: "bg-rose-500/15 text-rose-700 border-rose-500/25",
};

const PILLARS = [
  { icon: Lock, title: "PAN & KYC Data Handling", desc: "PAN, Aadhaar and bank details are encrypted at rest, masked by role, and access to full identifiers is logged for the SEBI audit trail." },
  { icon: FileCheck2, title: "SEBI / Exchange Compliance", desc: "UCC mapping, IPV timestamps, risk-disclosure acknowledgements and income-proof archives are retained per NSE·BSE·MCX norms." },
  { icon: KeyRound, title: "Role-Based Access Control", desc: "Admin, Management and RM scopes restrict who can view unmasked PAN, export MIS, or edit KYC — enforced on every action." },
  { icon: Server, title: "Immutable Audit Logs", desc: "Every view, export, dispatch and login attempt is written to an append-only ledger with actor, role, IP and severity." },
];

export function StockBrokingComplianceTab() {
  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-emerald-600" /> Compliance &amp; Data Security
        </h2>
        <p className="text-xs text-slate-500 mt-0.5">SEBI-oriented controls: KYC data handling, access control and a tamper-evident audit trail.</p>
      </div>

      {/* Pillars */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {PILLARS.map((p) => {
          const Icon = p.icon;
          return (
            <div key={p.title} className="p-4 rounded-2xl bg-white border border-slate-200 hover:border-emerald-500/30 transition-colors">
              <div className="w-9 h-9 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mb-3">
                <Icon className="w-4.5 h-4.5 text-emerald-600" />
              </div>
              <h3 className="text-sm font-bold text-slate-900 mb-1">{p.title}</h3>
              <p className="text-[11px] text-slate-500 leading-relaxed">{p.desc}</p>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Role matrix */}
        <div className="p-5 rounded-2xl bg-white border border-slate-200 lg:col-span-1">
          <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2 mb-4"><Eye className="w-4 h-4 text-emerald-600" /> Access Control Matrix</h3>
          <div className="space-y-3">
            {CRM_ROLES.map((r) => (
              <div key={r.id} className="p-3 rounded-xl bg-slate-50 border border-slate-200">
                <div className="text-xs font-bold text-slate-900">{r.label}</div>
                <div className="text-[10px] text-slate-500 mt-0.5">{r.scope}</div>
              </div>
            ))}
          </div>
          <div className="mt-4 p-3 rounded-xl bg-emerald-500/5 border border-emerald-500/20 text-[10px] text-emerald-700 flex items-start gap-2">
            <ShieldCheck className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            RM users always see PAN masked (XXXXX-nnnn-X). Unmask events are denied and logged.
          </div>
        </div>

        {/* Audit log */}
        <div className="p-5 rounded-2xl bg-white border border-slate-200 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2"><ScrollText className="w-4 h-4 text-emerald-600" /> Audit Trail</h3>
            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 border border-emerald-500/20 flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> Append-only
            </span>
          </div>
          <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
            {SEED_AUDIT_LOG.map((log) => (
              <div key={log.id} className="p-3 rounded-xl bg-slate-50 border border-slate-200 flex items-start gap-3">
                <div className={`mt-0.5 w-6 h-6 rounded-lg flex items-center justify-center shrink-0 ${log.severity === "critical" ? "bg-rose-500/15 text-rose-700" : log.severity === "warning" ? "bg-amber-500/15 text-amber-700" : "bg-slate-500/15 text-slate-600"}`}>
                  {log.severity === "info" ? <FileCheck2 className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-semibold text-slate-900">{log.action}</span>
                    <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded border shrink-0 ${SEVERITY_STYLE[log.severity]}`}>{log.severity}</span>
                  </div>
                  <p className="text-[11px] text-slate-500 mt-0.5">{log.entity}</p>
                  <p className="text-[10px] text-slate-500 mt-0.5 font-mono">{log.ts} · {log.actor} ({log.role}) · IP {log.ip}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <p className="text-[10px] text-slate-500 text-center">
        Demo audit entries are illustrative. Production wires to an immutable log store (e.g. WORM/S3 Object Lock) with SEBI-mandated retention.
      </p>
    </div>
  );
}
